import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { API_URL } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';

import { DelinquentClaimsStatisticPayload } from '../DelinquentClaimsStatistic.types';

const QUERY_KEY = 'GET_DELINQUENT_CLAIMS_STATISTIC';

const useGetDelinquentClaimsStatisticQuery = () => {
  const {
    observingOrganization: { type, id },
  } = useCurrentOrganization({ isLoadDefaultOrgData: true });

  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);

  const { isLoading, data, isSuccess } = useQuery(
    [QUERY_KEY, type, id],
    () => api.get(API_URL.METRICS_DELINQUENT_CLAIMS),
    {
      enabled: !isSystemLevel,
    }
  );

  const formattedData = useMemo<DelinquentClaimsStatisticPayload | null>(() => {
    const responseData = data?.data;

    if (responseData) {
      return {
        // @ts-ignore
        isShowed: responseData?.show_delinquent_claims_metrics ?? true,
        paidRequiringDocsCount: responseData?.paid_requiring_docs_count || 0,
        repaymentRequiredCount: responseData?.repayment_required_count || 0,
        lockedPursesCount: responseData?.locked_purses_count ?? null,
      };
    }

    return null;
  }, [data?.data]);

  const isEmpty = useMemo(() => {
    if (isSystemLevel) {
      return true;
    }
    if (isLoading) {
      return false;
    }
    if (isSuccess && !formattedData?.isShowed) {
      return true;
    }
    return false;
  }, [formattedData?.isShowed, isLoading, isSuccess, isSystemLevel]);
  return {
    isLoading,
    isEmpty,
    isSuccess,
    data: formattedData,
  };
};

export default useGetDelinquentClaimsStatisticQuery;
